"use client";

import React from "react";
import { CircleCheck as CheckCircle2, ShieldCheck, FileCheck2, Activity } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const analyticalSpecs = [{
  id: "spec-1",
  label: "Purity threshold",
  value: "≥98.0% standard verification by reverse-phase HPLC"
}, {
  id: "spec-2",
  label: "Mass verification",
  value: "Exact molecular weight confirmed within ±1 Da via ESI-MS"
}, {
  id: "spec-3",
  label: "Counter-ion monitoring",
  value: "Residual TFA and acetate levels screened to institutional tolerances"
}];
export default function Assurance() {
  const shouldReduceMotion = useReducedMotion();
  const fadeUp = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 12
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  return <section id="research-quality-assurance" data-nav="light" className="bg-background py-14 md:py-20 border-b border-border">
      <div className="mx-auto max-w-[1088px] px-6 md:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          {/* Left Column: Clinical Equipment & Synthesizer Imagery */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} variants={fadeUp} className="lg:col-span-6 relative">
            <div className="relative rounded-lg overflow-hidden border border-border bg-muted">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799047/img/analytical_hplc_cleanroom.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799047/img/analytical_hplc_cleanroom.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799047/img/analytical_hplc_cleanroom.png-wvc-sizes" alt="Analytical HPLC testing unit and borosilicate peptide synthesis vessels in a sterile cleanroom" loading="lazy" decoding="async" className="w-full h-[360px] sm:h-[420px] lg:h-[480px] object-cover object-center" />

              {/* Analytical Hairline Spec Overlay */}
              <div className="absolute top-4 left-4 right-4 flex items-center justify-between pointer-events-none">
                <div className="inline-flex items-center gap-2 rounded-sm border border-border/80 bg-background/95 px-2.5 py-1 backdrop-blur-xs">
                  <Activity className="size-3.5 text-primary" />
                  <span className="font-mono text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    HPLC / MS Validated
                  </span>
                </div>
                <div className="inline-flex items-center gap-1.5 rounded-sm border border-border/80 bg-background/95 px-2 py-1 backdrop-blur-xs">
                  <ShieldCheck className="size-3.5 text-primary" />
                  <span className="font-mono text-xs font-medium text-foreground">
                    Batch QA
                  </span>
                </div>
              </div>

              {/* Analytical Ticks Corner Accents */}
              <div className="absolute bottom-3 right-3 rounded-sm bg-background/95 border border-border px-3 py-1.5 backdrop-blur-xs flex items-center gap-2">
                <FileCheck2 className="size-3.5 text-primary" />
                <span className="font-mono text-xs text-muted-foreground">
                  COA on File for Every Lot
                </span>
              </div>
            </div>
          </motion.div>

          {/* Right Column: Technical Explanations & Tolerance Specs */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.2
        }} variants={fadeUp} className="lg:col-span-6 flex flex-col justify-center">
            {/* Eyebrow */}
            <div className="flex items-center gap-2 mb-3">
              <span className="h-px w-6 bg-primary" />
              <p className="text-xs font-bold tracking-[0.1em] text-primary uppercase font-default">
                Analytical Rigor
              </p>
            </div>

            {/* H2 Headline */}
            <h2 className="text-2xl sm:text-3xl lg:text-[2rem] font-semibold text-foreground tracking-tight leading-snug mb-4 font-default">
              Synthesis precision and multi-stage purification protocols
            </h2>

            {/* Explanatory Paragraphs */}
            <p className="text-sm sm:text-base text-muted-foreground leading-relaxed mb-4">
              Every sequence is synthesized using automated solid-phase platforms and validated through orthogonal analytical methods. Reverse-phase high-performance liquid chromatography screens for target fractions, while electrospray ionization mass spectrometry verifies correct sequence assembly.
            </p>
            <p className="text-sm sm:text-base text-muted-foreground leading-relaxed mb-6">
              Batch records, chromatograms, and mass spectra are catalogued for each lot to provide research laboratories in India with dependable chemical uniformity from synthesis to lyophilization.
            </p>

            {/* Three-item spec checklist */}
            <div className="space-y-3 mb-8 border-t border-b border-border py-4">
              {analyticalSpecs.map((spec, i) => <div key={spec.id} data-index={i} className="flex items-start gap-3 text-sm">
                  <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                  <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-2">
                    <span className="font-semibold text-foreground shrink-0 font-default">
                      {spec.label}:
                    </span>
                    <span className="text-muted-foreground font-mono text-xs sm:text-sm">
                      {spec.value}
                    </span>
                  </div>
                </div>)}
            </div>

            {/* Action Button */}
            <div>
              <Button asChild variant="secondary" size="lg" className="font-medium tracking-wide">
                <Link to="/ResearchInformationPage">Learn More</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}